import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  FolderKanban,
  Users,
  Settings,
  X,
  Loader2
} from 'lucide-react';
import apiService from './apiService';

const steps = [
  { id: 1, label: 'Basic Info', icon: FolderKanban },
  { id: 2, label: 'Team', icon: Users },
  { id: 3, label: 'Settings', icon: Settings },
  { id: 4, label: 'Review', icon: Check },
];

const CreateProjectWizard = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [availableMembers, setAvailableMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(true);

  // Load team members on mount
  useEffect(() => {
    const loadMembers = async () => {
      try {
        const response = await apiService.getTeamMembers();
        setAvailableMembers(response.members || []);
      } catch (error) {
        console.error('Failed to load team members:', error);
      } finally {
        setLoadingMembers(false);
      }
    };
    loadMembers();
  }, []);

  const [formData, setFormData] = useState({
    // Step 1: Basic Info
    name: '',
    description: '',
    client: '',
    startDate: '',
    dueDate: '',
    budget: '',
    priority: 'medium',

    // Step 2: Team
    selectedMembers: [],

    // Step 3: Settings
    visibility: 'team',
    enableNotifications: true,
    enableTimeTracking: false,
    requireApprovals: false,
    allowGuestAccess: false,
    tags: []
  });

  const [newTag, setNewTag] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const toggleMember = (memberId) => {
    setFormData(prev => ({
      ...prev,
      selectedMembers: prev.selectedMembers.includes(memberId)
        ? prev.selectedMembers.filter(id => id !== memberId)
        : [...prev.selectedMembers, memberId]
    }));
  };

  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const removeTag = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };

  const nextStep = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await apiService.createProject({
        name: formData.name,
        description: formData.description,
        client: formData.client,
        startDate: formData.startDate,
        dueDate: formData.dueDate,
        budget: formData.budget,
        priority: formData.priority,
        selectedMembers: formData.selectedMembers,
        visibility: formData.visibility,
        tags: formData.tags
      });
      navigate('/projects');
    } catch (error) {
      console.error('Failed to create project:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return formData.name.trim() !== '';
      case 2:
        return true; // Team selection is optional
      case 3:
        return true; // Settings are optional
      case 4:
        return true;
      default:
        return false;
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div>
            <h2 className="aut-wizard-content-title">Project Details</h2>
            <p className="aut-wizard-content-description">Enter the basic information about your project.</p>

            <form className="aut-form">
              <div className="aut-form-group">
                <label className="aut-label" htmlFor="name">Project Name *</label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  className="aut-input"
                  placeholder="Enter project name"
                  value={formData.name}
                  onChange={handleChange}
                />
              </div>

              <div className="aut-form-group">
                <label className="aut-label" htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  className="aut-textarea"
                  placeholder="Describe your project..."
                  value={formData.description}
                  onChange={handleChange}
                  rows={3}
                />
              </div>

              <div className="aut-form-row">
                <div className="aut-form-group">
                  <label className="aut-label" htmlFor="client">Client</label>
                  <input
                    id="client"
                    name="client"
                    type="text"
                    className="aut-input"
                    placeholder="Client name"
                    value={formData.client}
                    onChange={handleChange}
                  />
                </div>

                <div className="aut-form-group">
                  <label className="aut-label" htmlFor="budget">Budget</label>
                  <input
                    id="budget"
                    name="budget"
                    type="text"
                    className="aut-input"
                    placeholder="$0.00"
                    value={formData.budget}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="aut-form-row">
                <div className="aut-form-group">
                  <label className="aut-label" htmlFor="startDate">Start Date</label>
                  <input
                    id="startDate"
                    name="startDate"
                    type="date"
                    className="aut-input"
                    value={formData.startDate}
                    onChange={handleChange}
                  />
                </div>

                <div className="aut-form-group">
                  <label className="aut-label" htmlFor="dueDate">Due Date</label>
                  <input
                    id="dueDate"
                    name="dueDate"
                    type="date"
                    className="aut-input"
                    value={formData.dueDate}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="aut-form-group">
                <label className="aut-label" htmlFor="priority">Priority</label>
                <select
                  id="priority"
                  name="priority"
                  className="aut-select"
                  value={formData.priority}
                  onChange={handleChange}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
            </form>
          </div>
        );

      case 2:
        return (
          <div>
            <h2 className="aut-wizard-content-title">Team Members</h2>
            <p className="aut-wizard-content-description">Select team members to add to this project.</p>

            {loadingMembers ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                <Loader2 size={32} className="aut-spinner" style={{ animation: 'spin 1s linear infinite' }} />
              </div>
            ) : (
            <div className="aut-checkbox-group">
              {availableMembers.map((member) => (
                <div
                  key={member.id}
                  className="aut-checkbox-item"
                  style={{
                    padding: '16px',
                    background: formData.selectedMembers.includes(member.id) ? 'var(--aut-border-light)' : 'transparent',
                    borderRadius: 'var(--aut-radius)',
                    marginBottom: '8px'
                  }}
                  onClick={() => toggleMember(member.id)}
                >
                  <input
                    type="checkbox"
                    checked={formData.selectedMembers.includes(member.id)}
                    onChange={() => toggleMember(member.id)}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500 }}>{member.name}</div>
                    <div style={{ fontSize: '14px', color: 'var(--aut-text-secondary)' }}>{member.role}</div>
                  </div>
                </div>
              ))}
            </div>
            )}

            {formData.selectedMembers.length > 0 && (
              <div style={{ marginTop: '24px', padding: '16px', background: 'var(--aut-border-light)', borderRadius: 'var(--aut-radius)' }}>
                <span style={{ color: 'var(--aut-text-secondary)', fontSize: '14px' }}>
                  {formData.selectedMembers.length} member{formData.selectedMembers.length !== 1 ? 's' : ''} selected
                </span>
              </div>
            )}
          </div>
        );

      case 3:
        return (
          <div>
            <h2 className="aut-wizard-content-title">Project Settings</h2>
            <p className="aut-wizard-content-description">Configure how your project works.</p>

            <form className="aut-form">
              <div className="aut-form-group">
                <label className="aut-label" htmlFor="visibility">Visibility</label>
                <select
                  id="visibility"
                  name="visibility"
                  className="aut-select"
                  value={formData.visibility}
                  onChange={handleChange}
                >
                  <option value="private">Private - Only team members</option>
                  <option value="team">Team - All organization members</option>
                  <option value="public">Public - Anyone with link</option>
                </select>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '8px' }}>
                <label className="aut-checkbox-item">
                  <input
                    type="checkbox"
                    name="enableNotifications"
                    checked={formData.enableNotifications}
                    onChange={handleChange}
                  />
                  <div>
                    <div style={{ fontWeight: 500 }}>Enable Notifications</div>
                    <div style={{ fontSize: '14px', color: 'var(--aut-text-secondary)' }}>Send updates to team members</div>
                  </div>
                </label>

                <label className="aut-checkbox-item">
                  <input
                    type="checkbox"
                    name="enableTimeTracking"
                    checked={formData.enableTimeTracking}
                    onChange={handleChange}
                  />
                  <div>
                    <div style={{ fontWeight: 500 }}>Enable Time Tracking</div>
                    <div style={{ fontSize: '14px', color: 'var(--aut-text-secondary)' }}>Track time spent on tasks</div>
                  </div>
                </label>

                <label className="aut-checkbox-item">
                  <input
                    type="checkbox"
                    name="requireApprovals"
                    checked={formData.requireApprovals}
                    onChange={handleChange}
                  />
                  <div>
                    <div style={{ fontWeight: 500 }}>Require Approvals</div>
                    <div style={{ fontSize: '14px', color: 'var(--aut-text-secondary)' }}>Tasks need approval before completion</div>
                  </div>
                </label>

                <label className="aut-checkbox-item">
                  <input
                    type="checkbox"
                    name="allowGuestAccess"
                    checked={formData.allowGuestAccess}
                    onChange={handleChange}
                  />
                  <div>
                    <div style={{ fontWeight: 500 }}>Allow Guest Access</div>
                    <div style={{ fontSize: '14px', color: 'var(--aut-text-secondary)' }}>External users can view project</div>
                  </div>
                </label>
              </div>

              <div className="aut-form-group" style={{ marginTop: '24px' }}>
                <label className="aut-label">Tags</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    className="aut-input"
                    placeholder="Add a tag"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                  />
                  <button type="button" className="aut-btn aut-btn-secondary" onClick={addTag}>
                    Add
                  </button>
                </div>
                {formData.tags.length > 0 && (
                  <div className="aut-tags">
                    {formData.tags.map((tag) => (
                      <span key={tag} className="aut-tag">
                        {tag}
                        <span className="aut-tag-remove" onClick={() => removeTag(tag)}>
                          <X size={14} />
                        </span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </form>
          </div>
        );

      case 4:
        const selectedMemberNames = availableMembers
          .filter(m => formData.selectedMembers.includes(m.id))
          .map(m => m.name);

        return (
          <div>
            <h2 className="aut-wizard-content-title">Review & Create</h2>
            <p className="aut-wizard-content-description">Review your project details before creating.</p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <div>
                <h4 className="aut-detail-section-title">Basic Information</h4>
                <div className="aut-detail-field">
                  <div className="aut-detail-label">Project Name</div>
                  <div className="aut-detail-value">{formData.name || '-'}</div>
                </div>
                <div className="aut-detail-field">
                  <div className="aut-detail-label">Description</div>
                  <div className="aut-detail-value">{formData.description || '-'}</div>
                </div>
                <div className="aut-detail-field">
                  <div className="aut-detail-label">Client</div>
                  <div className="aut-detail-value">{formData.client || '-'}</div>
                </div>
                <div className="aut-detail-field">
                  <div className="aut-detail-label">Timeline</div>
                  <div className="aut-detail-value">
                    {formData.startDate || 'Not set'} - {formData.dueDate || 'Not set'}
                  </div>
                </div>
                <div className="aut-detail-field">
                  <div className="aut-detail-label">Priority</div>
                  <div className="aut-detail-value" style={{ textTransform: 'capitalize' }}>{formData.priority}</div>
                </div>
              </div>

              <div>
                <h4 className="aut-detail-section-title">Team</h4>
                <div className="aut-detail-value">
                  {selectedMemberNames.length > 0 ? selectedMemberNames.join(', ') : 'No team members selected'}
                </div>
              </div>

              <div>
                <h4 className="aut-detail-section-title">Settings</h4>
                <div className="aut-detail-field">
                  <div className="aut-detail-label">Visibility</div>
                  <div className="aut-detail-value" style={{ textTransform: 'capitalize' }}>{formData.visibility}</div>
                </div>
                <div className="aut-detail-field">
                  <div className="aut-detail-label">Features</div>
                  <div className="aut-detail-value">
                    {[
                      formData.enableNotifications && 'Notifications',
                      formData.enableTimeTracking && 'Time Tracking',
                      formData.requireApprovals && 'Approvals',
                      formData.allowGuestAccess && 'Guest Access'
                    ].filter(Boolean).join(', ') || 'None'}
                  </div>
                </div>
                {formData.tags.length > 0 && (
                  <div className="aut-detail-field">
                    <div className="aut-detail-label">Tags</div>
                    <div className="aut-tags">
                      {formData.tags.map((tag) => (
                        <span key={tag} className="aut-tag">{tag}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="aut-create-project-wizard">
      <Link to="/projects" className="aut-back-link">
        <ArrowLeft size={18} />
        <span>Back to Projects</span>
      </Link>

      <div className="aut-page-header">
        <h1 className="aut-page-title">Create New Project</h1>
        <p className="aut-page-description">Follow the steps below to set up your project.</p>
      </div>

      <div className="aut-wizard">
        {/* Steps Indicator */}
        <div className="aut-wizard-steps">
          {steps.map((step, index) => {
            const isCompleted = currentStep > step.id;
            const isActive = currentStep === step.id;

            return (
              <div
                key={step.id}
                className={`aut-wizard-step ${isActive ? 'aut-wizard-step-active' : ''} ${isCompleted ? 'aut-wizard-step-completed' : ''}`}
              >
                <div className="aut-wizard-step-number">
                  {isCompleted ? <Check size={18} /> : step.id}
                </div>
                <span className="aut-wizard-step-label">{step.label}</span>
              </div>
            );
          })}
        </div>

        {/* Step Content */}
        <div className="aut-wizard-content">
          {renderStepContent()}
        </div>

        {/* Navigation */}
        <div className="aut-wizard-actions">
          <button
            className="aut-btn aut-btn-secondary"
            onClick={prevStep}
            disabled={currentStep === 1}
            style={{ visibility: currentStep === 1 ? 'hidden' : 'visible' }}
          >
            <ArrowLeft size={18} />
            <span>Previous</span>
          </button>

          {currentStep < steps.length ? (
            <button
              className="aut-btn aut-btn-primary"
              onClick={nextStep}
              disabled={!canProceed()}
            >
              <span>Next</span>
              <ArrowRight size={18} />
            </button>
          ) : (
            <button
              className="aut-btn aut-btn-primary"
              onClick={handleSubmit}
              disabled={isSubmitting || !canProceed()}
            >
              {isSubmitting ? (
                <span>Creating...</span>
              ) : (
                <>
                  <Check size={18} />
                  <span>Create Project</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateProjectWizard;
